import { IJsonModel, IJsonRowNode, IJsonTabSetNode } from "flexlayout-react";

import getPanelsLayout from "./get_panels_layout";
import PanelType, { getPanelNodesByPanelType } from "../../models/domain/enums/panel_type";
import { InspectionMode } from "../../models/domain/enums/inspection_modes";
import { PanelFlags } from "../../models/domain/enums/panel_flag";

export type ModePanelConfiguration = {
  /** The tabs that share the Inspector's tabset, which the design calls "shared with mode". */
  modeTabset: PanelType[],
  photos: boolean
};

// The panel sizes the design gives, in the pixels of the figma's 1440x900 frame: the BIM and
// Photos panels side by side at 620 and 518, a 300 wide column of Inspector over Maps to their
// right, and the Inspector 404 tall. flexlayout divides the space a row has left after its
// splitters in proportion to its children's weights, so weights that are those pixel counts --
// and splitters as wide as the figma's 1px dividers -- put each panel on the figma's own number
// at the frame's size, and scale it from there.
const DIVIDER = 1;
// The divider is a drag handle as well as a line, so it keeps a hit area either side of it that
// leaves the panels as resizable as a thicker one would be.
const DIVIDER_GRAB_MARGIN = 4;

const BIM_WIDTH = 620;
const PHOTOS_WIDTH = 518;
const RIGHT_COLUMN_WIDTH = 300;
// Maps takes what the column has left under the Inspector, which is 396 of the 801 the viewer has
// at the frame's height -- the figma draws 372, in a frame whose two nav bars are 107 tall
// against this viewer's 99.
const INSPECTOR_HEIGHT = 404;
const MAPS_HEIGHT = 396;

// Resolved through a call rather than a lookup table: panel_type imports this module back for
// getDefaultPanelLayout, so PanelType is still empty while this module is first evaluated.
export function getPanelConfigurationForMode(inspectionMode: InspectionMode): ModePanelConfiguration {
  switch (inspectionMode) {
    case InspectionMode.inspect: {
      return {
        modeTabset: [PanelType.ELEMENT_INSPECTOR, PanelType.ELEMENT_GROUPS, PanelType.INSPECT_PANEL],
        photos: false
      };
    }
    case InspectionMode.bimPrep: {
      return {
        modeTabset: [PanelType.ELEMENT_INSPECTOR, PanelType.ELEMENT_GROUPS, PanelType.INSPECT_PANEL],
        photos: true
      };
    }
    // Visual and Progress share a panel set, and an unrecognised mode falls back to it.
    default: {
      return {
        modeTabset: [PanelType.ELEMENT_INSPECTOR, PanelType.INSPECT_PANEL],
        photos: true
      };
    }
  }
}

function modeTabsetPanels(configuration: ModePanelConfiguration, flags: PanelFlags): PanelType[] {
  return configuration.modeTabset.filter(panelType => panelType !== PanelType.ELEMENT_GROUPS || flags.groupsPanel);
}

export default function getPanelsLayoutForMode(inspectionMode: InspectionMode, flags: PanelFlags, tabEnableClose: boolean = true): IJsonModel {
  // A photos-only project has no BIM to configure per mode, so it keeps the layout it has today.
  if (flags.photosOnly) {
    return getPanelsLayout(flags, tabEnableClose);
  }

  const configuration = getPanelConfigurationForMode(inspectionMode);

  // Inspector over Maps, in a column down the right-hand side. flexlayout alternates orientation
  // with nesting, so a row nested in the layout's own row stacks its children instead of adding
  // two more columns.
  const rightColumn: IJsonRowNode = {
    type: "row",
    weight: RIGHT_COLUMN_WIDTH,
    children: [
      {
        type: "tabset",
        weight: INSPECTOR_HEIGHT,
        children: modeTabsetPanels(configuration, flags).map(getPanelNodesByPanelType),
        tabLocation: "top"
      },
      {
        type: "tabset",
        weight: MAPS_HEIGHT,
        children: [getPanelNodesByPanelType(PanelType.MINIMAP)],
        tabLocation: "top"
      }
    ]
  };

  // BIM and Photos are columns of the layout's own row, so they split the canvas side by side.
  // Without a Photos panel the BIM takes the width both of them had, the divider between them
  // included.
  const canvas: IJsonTabSetNode[] = [{
    type: "tabset",
    weight: configuration.photos ? BIM_WIDTH : BIM_WIDTH + DIVIDER + PHOTOS_WIDTH,
    children: [getPanelNodesByPanelType(PanelType.VIEWER)]
  }];

  if (configuration.photos) {
    canvas.push({
      type: "tabset",
      weight: PHOTOS_WIDTH,
      children: [getPanelNodesByPanelType(PanelType.PHOTO_VIEWER)],
      tabLocation: "top"
    });
  }

  return {
    global: { tabEnableClose, splitterSize: DIVIDER, splitterExtra: DIVIDER_GRAB_MARGIN },
    borders: [],
    layout: {
      type: "row",
      weight: 100,
      children: [...canvas, rightColumn]
    }
  };
}
